import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/api/client';
import { LogoFull } from '@/components/LogoFull';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { loginWithPasskey } from '@/lib/webauthn';
import { KeyIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '@/hooks/usePageTitle';
import { trackAnonymous } from '@/components/PostHogTracker';
import { isApiReturnTo, sanitizeReturnTo } from '@/utils/returnTo';

const PASSKEY_OPTIONS_URL = '/api/passkeys/login/options';
const PASSKEY_RESULT_URL = '/api/passkeys/login';

function isWebAuthnSupported(): boolean {
    return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}

export function LoginPage() {
    usePageTitle('Sign In');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [faxNumber, setFaxNumber] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);

    const { login, refreshProfile } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Set by the OAuth flow when the browser was sent here to sign in first.
    const returnTo = sanitizeReturnTo(searchParams.get('returnTo'));

    const locationState = location.state as {
        from?: Location;
        sessionExpired?: boolean;
    };
    // Don't redirect back to token-based action pages after login
    const fromPath = locationState?.from?.pathname;
    const from = fromPath?.startsWith('/email-change') || fromPath?.startsWith('/account/delete')
        ? '/dashboard'
        : (fromPath || '/dashboard');
    const sessionExpired = locationState?.sessionExpired;

    const webAuthnSupported = isWebAuthnSupported();

    function redirectAfterLogin() {
        if (returnTo === null) {
            navigate(from, { replace: true });
            return;
        }

        // An API path is not a router route: the server answers it with a redirect.
        if (isApiReturnTo(returnTo)) {
            window.location.assign(returnTo);
            return;
        }

        navigate(returnTo, { replace: true });
    }

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();
        setError(null);
        setIsSubmitting(true);

        try {
            await login(email, password, faxNumber);
            redirectAfterLogin();
        } catch (err) {
            // The server answers every failure with the same generic 401, so an
            // unverified account looks like a wrong password (no user enumeration).
            // Only rate limiting gets its own status.
            if (err instanceof ApiError && err.status === 429) {
                setError('Too many sign-in attempts. Please wait a few minutes and try again.');
                trackAnonymous('login_failed', { reason: 'rate_limited', method: 'password' });
            } else if (err instanceof ApiError) {
                setError('Invalid email or password');
                trackAnonymous('login_failed', { reason: 'invalid_credentials', method: 'password' });
            } else {
                setError('An unexpected error occurred');
                trackAnonymous('login_failed', { reason: 'unknown', method: 'password' });
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    async function handlePasskeyLogin() {
        setError(null);
        setIsPasskeyLoading(true);

        try {
            await loginWithPasskey(PASSKEY_OPTIONS_URL, PASSKEY_RESULT_URL);
            // Refresh the auth context after successful passkey login
            await refreshProfile();
            redirectAfterLogin();
        } catch (err) {
            let reason = 'unknown';
            if (err instanceof Error) {
                if (err.name === 'NotAllowedError') {
                    setError('Passkey authentication was cancelled');
                    reason = 'cancelled';
                } else if (err.name === 'SecurityError') {
                    setError('Security error: Please ensure you are on a secure connection');
                    reason = 'security_error';
                } else if (err.message.includes('No passkeys available') || err.message.includes('allowCredentials')) {
                    setError('No passkeys found. Please sign in with your password first, then add a passkey in settings.');
                    reason = 'no_passkeys';
                } else {
                    setError(err.message || 'Passkey authentication failed');
                    reason = 'server_error';
                }
            } else {
                setError('An unexpected error occurred');
            }
            trackAnonymous('login_failed', { reason, method: 'passkey' });
        } finally {
            setIsPasskeyLoading(false);
        }
    }

    const isLoading = isSubmitting || isPasskeyLoading;

    return (
        <div className="grid min-h-screen bg-gray-50 dark:bg-dark lg:grid-cols-2">
            <div className="order-2 flex flex-col justify-center bg-primary px-8 py-12 text-white lg:order-1 lg:px-16">
                <div className="mx-auto w-full max-w-md">
                    <a href="https://netfiscus.com" aria-label="Netfiscus website" className="dark mb-8 inline-block">
                        <LogoFull className="h-9 w-auto" />
                    </a>

                    <h1 className="mb-4 text-3xl font-bold leading-tight">
                        Welcome back to Netfiscus
                    </h1>
                    <p className="text-gray-300">
                        Sign in to see your accounts, assets and debts, and how your net worth
                        has moved since you last checked.
                    </p>
                </div>
            </div>

            <div className="order-1 flex items-center justify-center p-8 lg:order-2">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center lg:hidden">
                        <a href="https://netfiscus.com" aria-label="Netfiscus website">
                            <LogoFull className="h-10 w-auto mx-auto" />
                        </a>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                    <h2 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                        Sign in
                    </h2>
                    <p className="mb-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        Continue to your dashboard
                    </p>

                        {sessionExpired && !error && (
                            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
                                Your session has expired. Please sign in again.
                            </div>
                        )}

                        {error && (
                            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                                {error}
                            </div>
                        )}

                        {webAuthnSupported && (
                            <>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={isLoading}
                                    isLoading={isPasskeyLoading}
                                    onClick={handlePasskeyLogin}
                                    className="w-full"
                                    size="lg"
                                >
                                    <KeyIcon className="mr-2 h-5 w-5" />
                                    {isPasskeyLoading ? 'Authenticating...' : 'Sign in with Passkey'}
                                </Button>

                                <div className="my-6 flex items-center">
                                    <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                                    <span className="px-4 text-sm text-gray-500 dark:text-gray-400">or</span>
                                    <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                                </div>
                            </>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <input
                                type="text"
                                name="fax_number"
                                value={faxNumber}
                                onChange={(e) => setFaxNumber(e.target.value)}
                                autoComplete="off"
                                tabIndex={-1}
                                style={{ position: 'absolute', left: '-9999px' }}
                                aria-hidden="true"
                            />
                            <Input
                                type="email"
                                id="email"
                                label="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="you@example.com"
                                autoComplete="username"
                            />

                            <Input
                                type="password"
                                id="password"
                                label="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                placeholder="Enter your password"
                                autoComplete="current-password"
                            />

                            <div className="flex justify-end">
                                <Link
                                    to="/forgot-password"
                                    className="text-sm font-medium text-gray-600 underline hover:no-underline dark:text-gray-300"
                                >
                                    Forgot password?
                                </Link>
                            </div>

                            <Button
                                type="submit"
                                disabled={isLoading}
                                isLoading={isSubmitting}
                                className="w-full"
                                size="lg"
                            >
                                {isSubmitting ? 'Signing in...' : 'Sign In'}
                            </Button>
                        </form>

                        <div className="mt-6 text-center">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                Don't have an account?{' '}
                            </span>
                            <Link
                                to="/register"
                                className="text-sm font-medium text-gray-600 underline hover:no-underline dark:text-gray-300"
                            >
                                Create one
                            </Link>
                        </div>
                    </div>

                    <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        Securely manage your financial data
                    </p>
                </div>
            </div>
        </div>
    );
}
